import { useCallback, useEffect, useRef, useState } from 'react';
import { ProductInDepotType } from '../types/ProductSliceTypes';
import { ResponseProductListInDepotType } from '../types/TypesStore';

// Динамический импорт QuaggaJS для уменьшения размера бандла
let Quagga: any = null;

export interface BarcodeScannerOptions {
  onProductFound?: (product: ProductInDepotType) => void;
  onProductNotFound?: (barcode: string) => void;
  onError?: (error: string) => void;
  debounceTime?: number; // время задержки для предотвращения повторного срабатывания
  minBarcodeLength?: number; // минимальная длина баркода
  enableCamera?: boolean; // включить сканирование с камеры
  cameraConfig?: {
    width: number;
    height: number;
    facingMode?: 'user' | 'environment'; // фронтальная или задняя камера
  };
  alwaysListen?: boolean; // всегда слушать баркоды (даже без камеры)
}

export const useBarcodeScanner = (
  productsList?: ResponseProductListInDepotType,
  options: BarcodeScannerOptions = {}
) => {
  const {
    onProductFound,
    onProductNotFound,
    onError,
    debounceTime = 100,
    minBarcodeLength = 8,
    enableCamera = false,
    alwaysListen = true,
    cameraConfig = {
      width: 640,
      height: 480,
      facingMode: 'environment'
    }
  } = options;

  const [isScanning, setIsScanning] = useState(false);
  const [lastScannedBarcode, setLastScannedBarcode] = useState<string>('');
  const [scannedProducts, setScannedProducts] = useState<ProductInDepotType[]>([]);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [showManualInput, setShowManualInput] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const barcodeBufferRef = useRef<string>('');
  const quaggaInitializedRef = useRef<boolean>(false);
  const lastProcessedBarcodeRef = useRef<{ code: string; timestamp: number } | null>(null);

  /**
   * Поиск товара по баркоду в списке продуктов
   */
  const findProductByBarcode = useCallback((barcode: string): ProductInDepotType | null => {
    if (!productsList?.products || !barcode) return null;

    return productsList.products.find(product => 
      product.barcode === barcode.trim()
    ) || null;
  }, [productsList?.products]);

  /**
   * Загрузка QuaggaJS библиотеки
   */
  const loadQuaggaJS = useCallback(async () => {
    if (Quagga || quaggaInitializedRef.current) return Quagga;
    
    try {
      // Динамический импорт QuaggaJS
      const quaggaModule = await import('quagga');
      Quagga = quaggaModule.default;
      quaggaInitializedRef.current = true;
      return Quagga;
    } catch (error) {
      onError?.('Не удалось загрузить библиотеку сканирования баркодов');
      return null;
    }
  }, [onError]);

  /**
   * Проверка разрешений камеры
   */
  const checkCameraPermission = useCallback(async () => {
    try {
      // Проверяем поддержку getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        onError?.('Ваш браузер не поддерживает доступ к камере');
        return false;
      }

      // Запрашиваем разрешение на камеру
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: cameraConfig.width,
          height: cameraConfig.height,
          facingMode: cameraConfig.facingMode
        }
      });
      
      // Останавливаем тестовый поток
      stream.getTracks().forEach(track => track.stop());
      
      setCameraPermission('granted');
      return true;
    } catch (error: any) {
      
      if (error.name === 'NotAllowedError') {
        setCameraPermission('denied');
        onError?.('Доступ к камере запрещен. Разрешите доступ к камере в настройках браузера.');
      } else if (error.name === 'NotFoundError') {
        onError?.('Камера не найдена. Убедитесь, что камера подключена.');
      } else if (error.name === 'NotSupportedError') {
        onError?.('Ваш браузер не поддерживает доступ к камере.');
      } else {
        onError?.('Ошибка доступа к камере: ' + error.message);
      }
      
      return false;
    }
  }, [cameraConfig, onError]);

  /**
   * Обработка отсканированного баркода
   */
  const handleBarcodeScanned = useCallback((barcode: string, fromCamera: boolean = false) => {
    // Для камеры используем более мягкие правила (минимум 3 символа)
    const effectiveMinLength = fromCamera ? 3 : minBarcodeLength;
    
    if (!barcode || barcode.length < effectiveMinLength) {
      return;
    }

    // Защита от повторной обработки одного и того же баркода
    const now = Date.now();
    const lastProcessed = lastProcessedBarcodeRef.current;
    
    if (lastProcessed && lastProcessed.code === barcode) {
      const timeSinceLastScan = now - lastProcessed.timestamp;
      
      // Для камеры даём 2 секунды, для клавиатуры 1 секунду
      const cooldownTime = fromCamera ? 2000 : 1000;
      
      if (timeSinceLastScan < cooldownTime) {
        return;
      }
    }
    
    // Обновляем последний обработанный баркод
    lastProcessedBarcodeRef.current = { code: barcode, timestamp: now };

    const product = findProductByBarcode(barcode);
    
    if (product) {
      // Проверяем, не был ли уже добавлен этот товар
      const isAlreadyScanned = scannedProducts.some(p => p.id === product.id);
      
      if (!isAlreadyScanned) {
        setScannedProducts(prev => [...prev, product]);
        onProductFound?.(product);
        setShowManualInput(false); // Скрываем поле ввода при успешном сканировании
      }
    } else {
      onProductNotFound?.(barcode);
      setShowManualInput(true); // Показываем поле ввода при неудачном сканировании
    }
    
    setLastScannedBarcode(barcode);
  }, [findProductByBarcode, scannedProducts, onProductFound, onProductNotFound, minBarcodeLength]);

  /**
   * Обработчик ввода с клавиатуры (для ручного ввода баркодов)
   */
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Очищаем предыдущий таймаут
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Если нажата Enter, обрабатываем накопленный баркод
    if (event.key === 'Enter') {
      event.preventDefault();
      const barcode = barcodeBufferRef.current.trim();
      if (barcode) {
        handleBarcodeScanned(barcode);
        barcodeBufferRef.current = '';
      }
      return;
    }

    // Если нажат Escape, очищаем буфер
    if (event.key === 'Escape') {
      barcodeBufferRef.current = '';
      return;
    }

    // Добавляем символ в буфер
    if (event.key.length === 1) {
      barcodeBufferRef.current += event.key;
      
      // Устанавливаем таймаут для автоматической обработки (для сканеров)
      debounceTimeoutRef.current = setTimeout(() => {
        const barcode = barcodeBufferRef.current.trim();
        // Для физических сканеров обрабатываем баркод любой длины
        // minBarcodeLength проверяется внутри handleBarcodeScanned
        if (barcode) {
          handleBarcodeScanned(barcode);
          barcodeBufferRef.current = '';
        }
      }, debounceTime);
    }
  }, [handleBarcodeScanned, debounceTime]);

  /**
   * Обработчик фокуса на поле ввода
   */
  const handleInputFocus = useCallback(() => {
    setIsScanning(true);
  }, []);

  /**
   * Обработчик потери фокуса
   */
  const handleInputBlur = useCallback(() => {
    setIsScanning(false);
  }, []);

  /**
   * Обработчик изменения значения в поле ввода
   */
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    // Очищаем предыдущий таймаут
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Если значение достаточно длинное, обрабатываем его
    if (value.length >= minBarcodeLength) {
      debounceTimeoutRef.current = setTimeout(() => {
        handleBarcodeScanned(value);
        event.target.value = ''; // Очищаем поле после обработки
      }, debounceTime);
    }
  }, [handleBarcodeScanned, debounceTime, minBarcodeLength]);

  /**
   * Запуск сканирования с камеры
   */
  const startCameraScanning = useCallback(async () => {
    if (!enableCamera) {
      onError?.('Сканирование с камеры отключено');
      return false;
    }

    if (!videoRef.current) {
      onError?.('Элемент видео не найден. Убедитесь, что видео элемент отрендерен.');
      return false;
    }

    try {
      // Проверяем разрешения
      const hasPermission = await checkCameraPermission();
      if (!hasPermission) {
        return false;
      }

      // Загружаем QuaggaJS
      const quaggaInstance = await loadQuaggaJS();
      if (!quaggaInstance) {
        return false;
      }

      // Инициализируем Quagga
      quaggaInstance.init({
        inputStream: {
          name: "Live",
          type: "LiveStream",
          target: videoRef.current,
          constraints: {
            width: cameraConfig.width,
            height: cameraConfig.height,
            facingMode: cameraConfig.facingMode
          },
        },
        decoder: {
          readers: [
            "code_128_reader",
            "ean_reader",
            "ean_8_reader",
            "code_39_reader",
            "code_39_vin_reader",
            "codabar_reader",
            "upc_reader",
            "upc_e_reader",
            "i2of5_reader"
          ]
        },
        locate: true,
        locator: {
          patchSize: "large",  // Увеличиваем размер патчей
          halfSample: false    // Отключаем половинное сэмплирование для лучшего качества
        },
        numOfWorkers: 4,  // Увеличиваем количество воркеров
        frequency: 5,     // Уменьшаем частоту для более тщательного анализа
        debug: false
      }, async (err: any) => {
        if (err) {
          onError?.('Ошибка инициализации камеры: ' + err.message);
          return;
        }
        
        // Попробуем вручную подключить медиа-поток к видео элементу
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: cameraConfig.width,
              height: cameraConfig.height,
              facingMode: cameraConfig.facingMode
            }
          });
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
        } catch (streamError) {
          // Игнорируем ошибку, Quagga сам управляет потоком
        }
        
        // Запускаем процесс сканирования
        quaggaInstance.start();
        
        setIsCameraActive(true);
        setIsScanning(true);
      });

      // Обработчик обнаружения баркода
      quaggaInstance.onDetected((result: any) => {
        const code = result.codeResult.code;
        handleBarcodeScanned(code, true); // true = от камеры
      });

      return true;
    } catch (error) {
      onError?.('Не удалось запустить камеру: ' + (error as Error).message);
      return false;
    }
  }, [enableCamera, checkCameraPermission, loadQuaggaJS, cameraConfig, handleBarcodeScanned, onError]);

  /**
   * Остановка сканирования с камеры
   */
  const stopCameraScanning = useCallback(() => {
    // Останавливаем QuaggaJS
    if (Quagga && isCameraActive) {
      Quagga.stop();
    }
    
    // Останавливаем медиа-поток из видео элемента
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    
    setIsCameraActive(false);
    setIsScanning(false);
  }, [isCameraActive]);

  /**
   * Удаление товара из списка отсканированных
   */
  const removeScannedProduct = useCallback((productId: string) => {
    setScannedProducts(prev => prev.filter(p => p.id !== productId));
  }, []);

  /**
   * Очистка всех отсканированных товаров
   */
  const clearScannedProducts = useCallback(() => {
    setScannedProducts([]);
  }, []);

  /**
   * Ручное добавление товара по баркоду
   */
  const addProductByBarcode = useCallback((barcode: string) => {
    handleBarcodeScanned(barcode);
  }, [handleBarcodeScanned]);

  // Установка глобального обработчика клавиш (всегда активен)
  useEffect(() => {
    if (alwaysListen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [alwaysListen]); // Убираем handleKeyDown из зависимостей

  // Очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      // Останавливаем QuaggaJS
      if (Quagga) {
        Quagga.stop();
      }
      
      // Останавливаем все медиа-потоки
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
      
      // Очищаем таймаут
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Состояние
    isScanning,
    lastScannedBarcode,
    scannedProducts,
    isCameraActive,
    cameraPermission,
    showManualInput,
    
    // Рефы для привязки к элементам
    inputRef,
    videoRef,
    
    // Обработчики событий
    handleInputFocus,
    handleInputBlur,
    handleInputChange,
    
    // Методы управления
    addProductByBarcode,
    removeScannedProduct,
    clearScannedProducts,
    findProductByBarcode,
    
    // Методы камеры
    startCameraScanning,
    stopCameraScanning,
    checkCameraPermission,
    
    // Утилиты
    setIsScanning,
    setShowManualInput
  };
};
